import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  Car, 
  ClipboardList, 
  LogOut,
  Wrench,
  Menu,
  X,
  FileText,
  Download,
  Settings,
  Shield,
  UserCog,
  Package,
  ClipboardCheck,
  Factory,
  Cable,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CoWorkerWidget from "@/components/CoWorkerWidget";

// Super Admin email - must match backend
const SUPER_ADMIN_EMAIL = "test@werkstatt.de";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeInventur, setActiveInventur] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchInventur = () => {
      api.get("/api/inventur/current")
        .then((res) => { if (!cancelled) setActiveInventur(res.data); })
        .catch(() => {});
    };
    fetchInventur();
    const id = setInterval(fetchInventur, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;
  const isMitarbeiter = user?.role === "mitarbeiter";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const adminNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/kunden", icon: Users, label: "Kunden" },
    { path: "/fahrzeuge", icon: Car, label: "Fahrzeuge" },
    { path: "/auftraege", icon: ClipboardList, label: "Aufträge" },
    { path: "/lager", icon: Package, label: "Lager" },
    { path: "/produktion", icon: Factory, label: "Produktion" },
    { path: "/kabelbaeume", icon: Cable, label: "Kabelbäume" },
    { path: "/inventur", icon: ClipboardCheck, label: "Inventur" },
    { path: "/mitarbeiter", icon: UserCog, label: "Mitarbeiter" },
    { path: "/pdf-export", icon: Download, label: "Arbeitskarte PDF" },
    { path: "/einstellungen", icon: Settings, label: "Einstellungen" },
    ...(isSuperAdmin ? [{ path: "/admin/users", icon: Shield, label: "Admin" }] : []),
  ];

  const mitarbeiterNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/auftraege", icon: ClipboardList, label: "Meine Aufträge" },
  ];

  const navItems = isMitarbeiter ? mitarbeiterNavItems : adminNavItems;

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 border-b border-white/10 px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="bg-white/95 rounded-lg p-1 shadow-md">
            <img src="/vytech-logo-new.png" alt="Vytech" className="h-9 w-auto" />
          </div>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            VyWork
          </h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-button"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          w-64 sidebar flex flex-col overflow-y-auto
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none
          pt-16 lg:pt-0
        `}
        data-testid="sidebar"
      >
        {/* Logo - Desktop only */}
        <div className="hidden lg:block px-4 py-5 border-b border-slate-200" style={{ background: '#f1f5f9' }}>
          <div className="flex items-center gap-3">
            <img src="/vytech-logo-new.png" alt="Vytech" style={{ height: '60px', width: 'auto' }} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                VyWork
              </h1>
              <p className="text-xs text-slate-500">Auftragsverwaltung</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" data-testid="main-navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "active" : ""}`
              }
              onClick={handleNavClick}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 lg:pt-0">
        {activeInventur && (
          <div className="bg-blue-600 text-white px-4 py-2 text-sm flex items-center justify-between" data-testid="inventur-active-banner">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span><strong>Inventur läuft</strong> – Lagerbewegungen sind aktuell gesperrt.</span>
            </span>
            <button
              onClick={() => navigate("/inventur")}
              className="text-white underline hover:text-white/80 text-xs font-medium"
            >
              Zur Inventur →
            </button>
          </div>
        )}
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
      <CoWorkerWidget />
    </div>
  );
};

export default Layout;
